"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Calculator,
  ChevronRight,
  Headphones,
  Handshake,
  Home,
  LogOut,
  ScanLine,
  Search,
  Settings,
  ShoppingBag,
  X,
} from "lucide-react";
import type { AuthSession } from "@/lib/auth-session";
import { isPortalNavItemActive, portalNav } from "@/components/prv/data";
import navStyles from "@/components/prv/account-navigation.module.css";
import { userCenterNavLabel } from "@/lib/user-center-i18n";
import { useUserCenterLocale } from "@/lib/use-user-center-locale";

type DrawerAction = "product-search";

type DrawerItem = {
  label: string;
  icon: LucideIcon;
  href?: string;
  action?: DrawerAction;
  activePaths?: readonly string[];
  status?: "coming-soon";
};

function portalItem(href: string): DrawerItem {
  const item = portalNav.find((candidate) => candidate.href === href);
  if (!item) throw new Error(`PORTAL_NAV_ITEM_MISSING:${href}`);
  return item;
}

const guestGroups: DrawerItem[][] = [
  [
    { label: "Home", href: "/", icon: Home },
    { label: "Freight Calculator", href: "/shipping-estimate", icon: Calculator },
  ],
  [
    { label: "Product Search", action: "product-search", icon: Search },
    { label: "Affiliate", href: "/affiliate", icon: Handshake },
    { label: "Help Center", href: "/help-center", icon: Headphones },
  ],
];

const signedInGroups: DrawerItem[][] = [
  ...guestGroups,
  [portalItem("/user-center"), portalItem("/user-center/wishlist")],
  [
    { label: "Shopping Bag", href: "/cart", icon: ShoppingBag },
    portalItem("/user-center/orders"),
    portalItem("/user-center/warehouse"),
    portalItem("/user-center/rehearsal"),
    portalItem("/user-center/parcels"),
    portalItem("/user-center/after-sales"),
  ],
  [{ label: "DIY Order", href: "/diy-order", icon: ScanLine }],
  [portalItem("/user-center/wallet"), portalItem("/user-center/points"), portalItem("/user-center/coupons")],
  [portalItem("/help-center"), portalItem("/user-center/message"), portalItem("/user-center/support")],
];

type MobileAccountDrawerProps = {
  open: boolean;
  session: AuthSession | null;
  pathname: string;
  onClose: () => void;
  onOpenSearch: (mode: "product" | "image") => void;
  onOpenGuide?: () => void;
  onSignOut: () => void;
  signingOut?: boolean;
  navCounts?: { refundsPending: number; warehousePackable: number } | null;
};

export function MobileAccountDrawer({
  open,
  session,
  pathname,
  onClose,
  onOpenSearch,
  onSignOut,
  signingOut = false,
  navCounts = null,
}: MobileAccountDrawerProps) {
  const { locale, copy } = useUserCenterLocale();
  const drawerRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab" || !drawerRef.current) return;
      const focusable = Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      restoreFocusRef.current?.focus();
    };
  }, [open]);

  const groups = session ? signedInGroups : guestGroups;

  const handleAction = (action: DrawerAction) => {
    void action;
    onClose();
    onOpenSearch("product");
  };

  return (
    <>
      <button
        className={open ? "prv-mobile-drawer-backdrop is-open" : "prv-mobile-drawer-backdrop"}
        type="button"
        aria-label={copy.chrome.closeNavigation}
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
      />
      <aside
        id="prv-mobile-account-drawer"
        ref={drawerRef}
        className={open ? "prv-mobile-drawer is-open" : "prv-mobile-drawer"}
        role="dialog"
        aria-modal="true"
        aria-label={copy.chrome.mobileNavigation}
        aria-hidden={!open}
      >
        <div className="prv-mobile-drawer-head">
          <span className="prv-mobile-drawer-avatar" aria-hidden="true">
            {session ? session.name.charAt(0).toUpperCase() : "PRV"}
          </span>
          <span className="prv-mobile-drawer-identity">
            <strong>{session?.name || "PRVBUY"}</strong>
            <small>{session?.email || copy.chrome.privateAgent}</small>
          </span>
          {session ? (
            <Link
              className="prv-mobile-drawer-edit"
              href="/user-center/settings"
              aria-label={copy.chrome.editSettings}
              tabIndex={open ? 0 : -1}
              onClick={onClose}
            >
              <Settings size={19} aria-hidden="true" />
            </Link>
          ) : null}
          <button
            ref={closeButtonRef}
            className="prv-mobile-drawer-close"
            type="button"
            aria-label={copy.chrome.closeNavigation}
            tabIndex={open ? 0 : -1}
            onClick={onClose}
          >
            <X size={21} aria-hidden="true" />
          </button>
        </div>

        <nav className="prv-mobile-drawer-scroll" aria-label={copy.chrome.mobileNavigation}>
          {groups.map((group, groupIndex) => (
            <div className="prv-mobile-drawer-group" key={`drawer-group-${groupIndex}`}>
              {group.map((item) => {
                const Icon = item.icon;
                const active =
                  item.href === "/"
                    ? pathname === "/"
                    : Boolean(item.href && isPortalNavItemActive(pathname, { href: item.href, activePaths: item.activePaths }));
                const count = item.href === "/user-center/after-sales" ? navCounts?.refundsPending : item.href === "/user-center/warehouse" ? navCounts?.warehousePackable : undefined;
                if (item.action) {
                  return (
                    <button
                      type="button"
                      key={item.label}
                      tabIndex={open ? 0 : -1}
                      onClick={() => handleAction(item.action!)}
                    >
                      <Icon size={20} aria-hidden="true" />
                      <span>{item.action === "product-search" ? copy.navigation.search : item.label}</span>
                      <ChevronRight size={16} aria-hidden="true" />
                    </button>
                  );
                }

                return (
                  <Link
                    className={active ? "is-active" : undefined}
                    href={item.href!}
                    key={item.label}
                    aria-current={active ? "page" : undefined}
                    tabIndex={open ? 0 : -1}
                    onClick={onClose}
                  >
                    <Icon size={20} aria-hidden="true" />
                    <span className={navStyles.itemLabel}>
                      <span>{item.href && item.href in copy.navigation ? userCenterNavLabel(locale, item.href as keyof typeof copy.navigation) : item.label}</span>
                      {item.status === "coming-soon" ? (
                        <span className={navStyles.badge}>{copy.chrome.comingSoon}</span>
                      ) : count !== undefined || item.href === "/user-center/after-sales" || item.href === "/user-center/warehouse" ? (
                        <span className={navStyles.countBadge} data-loaded={count !== undefined ? "true" : "false"}>{count ?? 0}</span>
                      ) : null}
                    </span>
                    <ChevronRight size={16} aria-hidden="true" />
                  </Link>
                );
              })}
            </div>
          ))}

        </nav>

        <div className="prv-mobile-drawer-footer">
          {session ? (
            <button
              className="prv-mobile-drawer-signout"
              type="button"
              tabIndex={open ? 0 : -1}
              disabled={signingOut}
              aria-busy={signingOut}
              onClick={onSignOut}
            >
              <LogOut size={20} aria-hidden="true" />
              <span>{signingOut ? copy.chrome.loggingOut : copy.chrome.logOut}</span>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          ) : (
            <Link
              className="prv-mobile-drawer-login"
              href="/login"
              tabIndex={open ? 0 : -1}
              onClick={onClose}
            >
              <span>{copy.chrome.logIn}</span>
              <ChevronRight size={17} aria-hidden="true" />
            </Link>
          )}
        </div>
      </aside>
    </>
  );
}
